import { useState } from "react";
import { getDurationOptions } from "../utils/constants";
import {
  exceedsSixMonths,
  getDefaultDurationValue,
  getDurationDisplayValue,
  getDurationRange,
} from "../../../utils/constants/durationDefaults";
import CheckIcon from "../../../assets/svg/check-icon";
import Button from "../../Button/Button";
import SingleDatePicker from "../../SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../../assets/svg/calendar-icon";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCommunicationAnalyticsDuration,
  clearCommunicationsDuration,
  clearCrmLeadsDuration,
  clearCrmInboxDuration,
  clearPlaygroundDuration,
  FilterState,
  selectCommunicationsDuration,
  setCommunicationAnalyticsDuration,
  setCommunicationsDuration,
  setCrmLeadsDuration,
  setCrmInboxDuration,
  setPlaygroundDuration,
} from "../../../redux/slices/filterSlice";
import {
  setPmsLogsDuration,
  clearPmsLogsDuration,
  selectPmsLogsDuration,
} from "../../../redux/slices/pmsFilterSlice";
import { format, endOfDay, startOfDay } from "date-fns";

const DurationFilter = ({
  onClose,
  crmLeads,
  playground,
  communicationAnalytics,
  isPmsLogs,
  isCrmInbox,
}: {
  onClose: () => void;
  crmLeads?: boolean;
  playground?: boolean;
  communicationAnalytics?: boolean;
  isPmsLogs?: boolean;
  isCrmInbox?: boolean;
}) => {
  const dispatch = useDispatch();
  const pmsLogsDuration = useSelector(selectPmsLogsDuration);
  const durationState = useSelector(
    isPmsLogs
      ? () => pmsLogsDuration
      : isCrmInbox
        ? (state: { filter: FilterState }) => state.filter.crmInbox.duration
        : crmLeads
          ? (state: { filter: FilterState }) => state.filter.crmLeads.duration
          : playground
            ? (state: { filter: FilterState }) =>
                state.filter.playground.duration
            : communicationAnalytics
              ? (state: { filter: FilterState }) =>
                  state.filter.communicationAnalytics.duration
              : selectCommunicationsDuration
  );

  // Track both current selection and last applied value
  const [selectedOption, setSelectedOption] = useState<string>(
    durationState.value as string
  );
  const [lastAppliedOption, setLastAppliedOption] = useState<string>(
    durationState.value as string
  );
  const [lastAppliedFromDate, setLastAppliedFromDate] = useState<Date | null>(
    durationState.startDate ? new Date(durationState.startDate) : null
  );
  const [lastAppliedToDate, setLastAppliedToDate] = useState<Date | null>(
    durationState.endDate ? new Date(durationState.endDate) : null
  );
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(
    durationState.startDate ? new Date(durationState.startDate) : null
  );
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(
    durationState.endDate ? new Date(durationState.endDate) : null
  );
  const [openCalendar, setOpenCalendar] = useState<"from" | "to" | null>(null);
  const [isRangeExceedingSixMonths, setIsRangeExceedingSixMonths] =
    useState(false);

  const defaultOption = getDefaultDurationValue();
  const durationOptions = getDurationOptions();

  const haveDatesChanged = () => {
    if (selectedOption !== "custom") return false;

    const formatDate = (date: Date | null) => {
      return date ? format(date, "yyyy-MM-dd") : null;
    };

    const currentFromDate = formatDate(selectedFromDate);
    const currentToDate = formatDate(selectedToDate);
    const lastFromDate = formatDate(lastAppliedFromDate);
    const lastToDate = formatDate(lastAppliedToDate);

    return currentFromDate !== lastFromDate || currentToDate !== lastToDate;
  };

  // Check if the current date range exceeds 6 months
  const checkDateRangeValidity = (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    if (fromDate && toDate) {
      setIsRangeExceedingSixMonths(exceedsSixMonths(fromDate, toDate));
    } else {
      setIsRangeExceedingSixMonths(false);
    }
  };

  const handleDateChange = (date: Date | null, isFromDate: boolean) => {
    const now = new Date();

    if (date && date > now) {
      return;
    }

    if (isFromDate) {
      if (date && selectedToDate && date > selectedToDate) {
        return;
      }

      setSelectedFromDate(date);
      checkDateRangeValidity(date, selectedToDate);
    } else {
      if (date && selectedFromDate && date < selectedFromDate) {
        return;
      }

      const endOfDayDate = date ? endOfDay(date) : null;
      setSelectedToDate(endOfDayDate);
      checkDateRangeValidity(selectedFromDate, endOfDayDate);
    }

    setSelectedOption("custom");
  };

  const handleApply = () => {
    const displayValue = getDurationDisplayValue(selectedOption);

    if (selectedFromDate && selectedToDate && isRangeExceedingSixMonths) {
      return;
    }

    setLastAppliedOption(selectedOption);
    setLastAppliedFromDate(selectedFromDate);
    setLastAppliedToDate(selectedToDate);

    let startDateUTC = "";
    let endDateUTC = "";

    if (selectedOption === "last-24-hours") {
      startDateUTC = selectedFromDate
        ? new Date(selectedFromDate).toISOString()
        : "";

      endDateUTC = selectedToDate ? new Date(selectedToDate).toISOString() : "";
    } else {
      startDateUTC = selectedFromDate
        ? startOfDay(selectedFromDate)?.toISOString()
        : "";

      endDateUTC = selectedToDate
        ? endOfDay(selectedToDate)?.toISOString()
        : "";
    }
    const durationPayload = {
      startDate: startDateUTC,
      endDate: endDateUTC,
      displayValue: displayValue,
      value: selectedOption,
    };

    if (isPmsLogs) {
      dispatch(setPmsLogsDuration(durationPayload));
    } else if (isCrmInbox) {
      dispatch(setCrmInboxDuration(durationPayload));
    } else if (crmLeads) {
      dispatch(setCrmLeadsDuration(durationPayload));
    } else if (playground) {
      dispatch(setPlaygroundDuration(durationPayload));
    } else if (communicationAnalytics) {
      dispatch(setCommunicationAnalyticsDuration(durationPayload));
    } else {
      dispatch(setCommunicationsDuration(durationPayload));
    }

    onClose();
  };

  const handleClear = () => {
    setLastAppliedOption("");
    if (isPmsLogs) {
      dispatch(clearPmsLogsDuration());
    } else if (isCrmInbox) {
      dispatch(clearCrmInboxDuration());
    } else if (crmLeads) {
      dispatch(clearCrmLeadsDuration());
    } else if (playground) {
      dispatch(clearPlaygroundDuration());
    } else if (communicationAnalytics) {
      dispatch(clearCommunicationAnalyticsDuration());
    } else {
      dispatch(clearCommunicationsDuration());
    }
    onClose();
  };

  const handleDurationOptionClick = (optionValue: string) => {
    if (optionValue === selectedOption) {
      return;
    }
    setSelectedOption(optionValue);

    // "custom" keeps whatever the user already picked; every other preset resolves to a range.
    const { from, to } = getDurationRange(optionValue, new Date());
    const fromDate = optionValue === "custom" ? selectedFromDate : from;
    const toDate = optionValue === "custom" ? selectedToDate : to;

    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    if (fromDate && toDate) {
      checkDateRangeValidity(fromDate, toDate);
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-2 px-3">
      <h2 className="text-md  text-heading-text dark:text-heading-text-dk py-1.5">
        Quick Filters
      </h2>
      {durationOptions.map((option) => (
        <div
          key={option.value}
          onClick={() => handleDurationOptionClick(option.value)}
          className={`flex items-center justify-between border cursor-pointer ${
            selectedOption === option.value
              ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
              : "border-border-gray dark:border-border-gray-dk"
          } rounded-lg py-[13.5px] px-3`}
        >
          <div className="flex items-center gap-2">
            <span className="">{option.icon}</span>
            <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              {option.content}
            </h3>
          </div>
          {selectedOption === option.value && (
            <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
          )}
        </div>
      ))}

      <div className="flex flex-col gap-3 border-t border-border-gray dark:border-border-gray-dk my-2 pt-3">
        <h2 className="text-md  text-heading-text dark:text-heading-text-dk">
          Pick a custom date
        </h2>

        <div className="flex items-center gap-2">
          <SingleDatePicker
            label="From"
            labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
            Icon={CalendarIcon}
            value={selectedFromDate}
            onChange={(date) => handleDateChange(date, true)}
            containerClassName="w-full"
            maxDate={selectedToDate ? new Date(selectedToDate) : new Date()}
            isOpen={openCalendar === "from"}
            onOpenChange={(isOpen) => setOpenCalendar(isOpen ? "from" : null)}
            inputClassName={`text-base font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border px-[12px] py-[8px] rounded-[8px] ${
              selectedFromDate && selectedToDate && isRangeExceedingSixMonths
                ? "border-red-500 dark:border-red-500"
                : "border-border-gray dark:border-border-gray-dk"
            }`}
          />

          <div className="w-full">
            <SingleDatePicker
              label="To"
              labelClassName="text-md  text-verba-field-label dark:text-verba-field-label-dk"
              Icon={CalendarIcon}
              value={selectedToDate}
              onChange={(date) => handleDateChange(date, false)}
              containerClassName="w-full"
              minDate={
                selectedFromDate ? new Date(selectedFromDate) : undefined
              }
              calendarContainerClassName=""
              maxDate={new Date()}
              isOpen={openCalendar === "to"}
              onOpenChange={(isOpen) => setOpenCalendar(isOpen ? "to" : null)}
              // calendarContainerClassName="!-translate-x-[160px]"
              inputClassName={`text-base font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border px-[12px] py-[8px] rounded-[8px] ${
                selectedFromDate && selectedToDate && isRangeExceedingSixMonths
                  ? "border-red-500 dark:border-red-500"
                  : "border-border-gray dark:border-border-gray-dk"
              }`}
            />
          </div>
        </div>
      </div>
      {isRangeExceedingSixMonths && (
        <div className="text-sm text-red-500 dark:text-red-400 font-medium">
          Date range cannot exceed 6 months
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          onClick={handleClear}
          disabled={
            selectedOption === defaultOption
          }
          innerText="Clear"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          onClick={handleApply}
          innerText="Apply"
          disabled={
            isRangeExceedingSixMonths ||
            (selectedOption === lastAppliedOption && !haveDatesChanged())
          }
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default DurationFilter;
